import { z } from 'zod';
import { CarStatus } from '@/types/car/car-status.type.ts';
import { CarBodyType } from '@/types/car/car-body-type.type.ts';
import { CarEngineType } from '@/types/car/car-engine-type.type.ts';
import { CarTransmissionType } from '@/types/car/car-transmission-type.type.ts';
import { CarDriveType } from '@/types/car/car-drive-type.type.ts';
import { CarAdmissionType } from '@/types/car/admission-type.type.ts';
import { CarNdsType } from '@/types/car/car-nds-type.ts';
import { CarPhoto, CarPhotoSchema } from '@/types/car/car-photo.type.ts';
import { CarDocument, CarDocumentSchema } from '@/types/car/car-document.type.ts';
import { CarLastAuctionListItem } from '@/types/auction.type.ts';
import { CarVideo, CarVideoSchema } from '@/types/car/car-video.type.ts';
import { CarDamage, CarDamageSchema } from '@/types/car/car-damage.type.ts';
import { ComplectationOption, ComplectationOptionsSchema } from '@/types/complectation.type.ts';

export type Car = {
    id: number,
    status: CarStatus,
    vin: string,
    body_type: CarBodyType,
    body_color: string,
    engine_type: CarEngineType,
    transmission_type: CarTransmissionType,
    drive_type: CarDriveType,
    engine_volume: number,
    engine_power: number,
    production_year: number,
    mileage: number,
    pts_number: string,
    additional_equipment?: string | null,
    start_price: number,
    owners_count: number | null,
    months_with_last_owner?: number | null,
    was_owned_by_individual?: boolean,
    exist_dtp: boolean,
    is_pledged: boolean,
    is_stolen: boolean,
    admission_type: CarAdmissionType,
    nds_type: CarNdsType,
    comment?: string | null,
    tags: string[],
    created_at: string,
    brand: {
        id: number,
        name: string,
    },
    model: {
        id: number,
        name: string,
    },
    model_code: {
        id: number,
        model_code: string | null,
    } | null,
    complectation: {
        id: number | null,
        code: string | null,
    },
    dealer: {
        id: number | null,
        dealer_name: string | null,
        city_name: string | null,
    },
    creator?: {
        id: number | null,
        name: string | null,
    },
    photos?: CarPhoto[],
    documents?: CarDocument[],
    lastAuction?: CarLastAuctionListItem,
    videos?: CarVideo[],
    damages?: CarDamage[],
    options?: ComplectationOption[],
};

export const CarSchema = z.object({
    id: z.number(),
    status: z.nativeEnum(CarStatus),
    vin: z.string(),
    body_type: z.nativeEnum(CarBodyType),
    body_color: z.string(),
    engine_type: z.nativeEnum(CarEngineType),
    transmission_type: z.nativeEnum(CarTransmissionType),
    drive_type: z.nativeEnum(CarDriveType),
    engine_volume: z.number(),
    engine_power: z.number(),
    production_year: z.number(),
    mileage: z.number(),
    pts_number: z.string(),
    additional_equipment: z.string().nullable().optional(),
    start_price: z.number(),
    owners_count: z.number().nullable(),
    months_with_last_owner: z.number().nullable().optional(),
    was_owned_by_individual: z.boolean().optional(),
    exist_dtp: z.boolean(),
    is_pledged: z.boolean(),
    is_stolen: z.boolean(),
    admission_type: z.nativeEnum(CarAdmissionType),
    nds_type: z.nativeEnum(CarNdsType),
    comment: z.string().nullable().optional(),
    tags: z.array(z.string()),
    created_at: z.string(),
    brand: z.object({
        id: z.number(),
        name: z.string(),
    }),
    model: z.object({
        id: z.number(),
        name: z.string(),
    }),
    model_code: z.object({
        id: z.number(),
        model_code: z.string().nullable(),
    }).nullable(),
    complectation: z.object({
        id: z.number().nullable(),
        code: z.string().nullable(),
    }),
    dealer: z.object({
        id: z.number().nullable(),
        dealer_name: z.string().nullable(),
        city_name: z.string().nullable(),
    }),
    creator: z.object({
        id: z.number().nullable(),
        name: z.string().nullable(),
    }).optional(),
    photos: z.array(CarPhotoSchema).optional(),
    documents: z.array(CarDocumentSchema).optional(),
    videos: z.array(CarVideoSchema).optional(),
    damages: z.array(CarDamageSchema).optional(),
    options: ComplectationOptionsSchema.optional(),
});
